import { Html, Line, OrbitControls, useGLTF } from "@react-three/drei";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Component, Suspense, useEffect, useMemo, useRef, type ReactNode } from "react";
import * as THREE from "three";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import type { LayerId, SneezeStage, StageId } from "../content/sneeze";

export interface SelectedStructure {
  id: string;
  name: string;
  system: string;
  description: string;
  source: string;
}

interface AnatomySceneProps {
  stageId: StageId;
  focus: SneezeStage["focus"];
  stageProgress: number;
  layers: Record<LayerId, boolean>;
  surfaceOpacity: number;
  exploring: boolean;
  reducedMotion: boolean;
  motionActive: boolean;
  onExplore: () => void;
  onSelect: (structure: SelectedStructure) => void;
}

type ModelKind = "skin" | "lung" | "trachea" | "larynx" | "mouth" | "eye";

const modelUrl = (file: string) => `${import.meta.env.BASE_URL}models/${file}`;

export const MODEL_META: Record<ModelKind, SelectedStructure> = {
  skin: {
    id: "skin",
    name: "Surface anatomy",
    system: "Integumentary system",
    description:
      "A translucent reference surface provides spatial context without hiding the airway beneath it.",
    source: "NIH Human Reference Atlas · Visible Human Male",
  },
  lung: {
    id: "lungs",
    name: "Lungs and intrapulmonary bronchi",
    system: "Respiratory system",
    description:
      "The lungs expand during the preparatory inspiration and recoil during the expulsive phase.",
    source: "NIH HRA lung reference object · CC BY 4.0",
  },
  trachea: {
    id: "trachea",
    name: "Trachea",
    system: "Conducting airway",
    description:
      "The trachea conducts the pressure-driven jet between the larynx and branching bronchi.",
    source: "NIH HRA trachea reference object · CC BY 4.0",
  },
  larynx: {
    id: "larynx",
    name: "Larynx",
    system: "Upper airway",
    description:
      "The vocal folds close the glottis during compression, then open abruptly for release.",
    source: "NIH HRA larynx reference object · CC BY 4.0",
  },
  mouth: {
    id: "mouth",
    name: "Oral cavity",
    system: "Upper airway",
    description:
      "The segmented palate, tongue, teeth, gingiva, and salivary structures define one route for the expelled jet.",
    source: "NIH HRA mouth reference object · CC BY 4.0",
  },
  eye: {
    id: "eye",
    name: "Right eye",
    system: "Ocular system",
    description:
      "The globe is shown with a subtle blink-derived movement. Dramatic upward rolling is not supported as a universal sneeze motion.",
    source: "NIH HRA right-eye reference object · CC BY 4.0",
  },
};

const CAMERA_PRESETS: Record<
  SneezeStage["focus"],
  { position: THREE.Vector3Tuple; target: THREE.Vector3Tuple }
> = {
  head: { position: [1.45, 2.08, 3.35], target: [0, 1.98, 0.18] },
  brainstem: { position: [-1.55, 1.95, 3.45], target: [0, 1.92, 0.05] },
  thorax: { position: [1.65, 1.15, 4.15], target: [0, 1.16, -0.13] },
  larynx: { position: [1.15, 1.72, 3.2], target: [0, 1.7, 0.08] },
  airway: { position: [-1.25, 1.82, 3.35], target: [0, 1.8, 0.22] },
  body: { position: [1.9, 0.78, 5.65], target: [0, 0.72, -0.02] },
};

function materialFor(kind: ModelKind, name: string) {
  const key = name.toLowerCase();

  if (kind === "skin") {
    return new THREE.MeshPhysicalMaterial({
      color: 0x9b7568,
      roughness: 0.78,
      metalness: 0,
      transparent: true,
      opacity: 0.16,
      depthWrite: false,
      side: THREE.DoubleSide,
      clearcoat: 0.05,
    });
  }

  if (kind === "lung") {
    const right = key.includes("_r") || key.includes("right");
    return new THREE.MeshPhysicalMaterial({
      color: right ? 0xca5a64 : 0xdc7276,
      roughness: 0.58,
      clearcoat: 0.08,
      clearcoatRoughness: 0.72,
      side: THREE.DoubleSide,
    });
  }

  if (kind === "trachea") {
    return new THREE.MeshPhysicalMaterial({
      color: key.includes("cartilage") ? 0xa9c8bf : 0xe59a8b,
      roughness: 0.5,
      clearcoat: 0.12,
      side: THREE.DoubleSide,
    });
  }

  if (kind === "larynx") {
    const muscle = key.includes("muscle") || key.includes("vocal");
    return new THREE.MeshPhysicalMaterial({
      color: muscle ? 0xc84e5d : 0xf0bd73,
      roughness: 0.48,
      emissive: new THREE.Color(0x35100c),
      emissiveIntensity: 0.15,
      clearcoat: 0.1,
      side: THREE.DoubleSide,
    });
  }

  if (kind === "mouth") {
    let color = 0xd97b7e;
    if (key.includes("teeth")) color = 0xeee5d1;
    else if (key.includes("mandible") || key.includes("palate")) color = 0xd9c0a0;
    else if (key.includes("gland")) color = 0xe8a65e;
    else if (key.includes("tongue")) color = 0xb83f54;
    return new THREE.MeshPhysicalMaterial({
      color,
      roughness: 0.56,
      clearcoat: key.includes("teeth") ? 0.22 : 0.05,
      side: THREE.DoubleSide,
    });
  }

  let color = 0xe7e3d7;
  let transparent = false;
  let opacity = 1;
  if (key.includes("iris")) color = 0x6ea89f;
  else if (key.includes("pupil")) color = 0x07090a;
  else if (key.includes("retina") || key.includes("choroid")) color = 0x8f2636;
  else if (key.includes("lens") || key.includes("cornea") || key.includes("humor")) {
    color = 0xa9dddc;
    transparent = true;
    opacity = 0.24;
  }
  return new THREE.MeshPhysicalMaterial({
    color,
    roughness: 0.34,
    transmission: transparent ? 0.18 : 0,
    transparent,
    opacity,
    depthWrite: !transparent,
    clearcoat: 0.2,
    side: THREE.DoubleSide,
  });
}

function ModelLayer({
  url,
  kind,
  opacity = 1,
  stageId,
  stageProgress,
  onSelect,
}: {
  url: string;
  kind: ModelKind;
  opacity?: number;
  stageId: StageId;
  stageProgress: number;
  onSelect: (structure: SelectedStructure) => void;
}) {
  const { scene } = useGLTF(url);
  const pivotRef = useRef<THREE.Group>(null);
  const materialsRef = useRef<THREE.MeshPhysicalMaterial[]>([]);

  const { model, materials } = useMemo(() => {
    const clone = scene.clone(true);
    const clonedMaterials: THREE.MeshPhysicalMaterial[] = [];

    clone.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      const material = materialFor(kind, object.name || object.parent?.name || kind);
      object.material = material;
      object.castShadow = false;
      object.receiveShadow = false;
      clonedMaterials.push(material);
    });

    return { model: clone, materials: clonedMaterials };
  }, [kind, scene]);

  useEffect(() => {
    materialsRef.current = materials;
    return () => {
      materialsRef.current = [];
      for (const material of materials) material.dispose();
    };
  }, [materials]);

  useEffect(() => {
    if (kind !== "skin") return;
    // Three.js materials are imperative render resources; updating their live
    // uniforms is the intended R3F animation path, not React state mutation.
    /* eslint-disable react-hooks/immutability */
    for (const material of materialsRef.current) {
      material.opacity = opacity;
      material.depthWrite = opacity > 0.72;
      material.needsUpdate = true;
    }
    /* eslint-enable react-hooks/immutability */
  }, [kind, opacity]);

  useFrame(({ clock }) => {
    const pivot = pivotRef.current;
    if (!pivot) return;

    if (kind === "lung") {
      let breath = 0;
      if (stageId === "inspiration") breath = THREE.MathUtils.smoothstep(stageProgress, 0, 1);
      if (stageId === "compression") breath = 1 - stageProgress * 0.22;
      if (stageId === "release") breath = 0.78 * (1 - stageProgress);
      if (stageId === "recovery") breath = 0.08 * Math.sin(stageProgress * Math.PI * 2);
      const scale = 1 + breath * 0.055;
      pivot.scale.set(1 + breath * 0.032, scale, 1 + breath * 0.042);
    }

    if (kind === "eye") {
      const blinkMotion =
        stageId === "compression" || stageId === "release"
          ? Math.sin(Math.min(stageProgress * 1.7, 1) * Math.PI)
          : 0;
      pivot.rotation.x = THREE.MathUtils.degToRad(1.2) * blinkMotion;
      pivot.rotation.y = THREE.MathUtils.degToRad(1) * blinkMotion;
      pivot.rotation.z = 0;
    }

    if (kind === "larynx") {
      const active = stageId === "compression" || stageId === "release";
      const pulse = active ? 1.15 + Math.sin(clock.elapsedTime * 5) * 0.25 : 0.12;
      // See the material ownership note above: this writes a GPU uniform on
      // an object exclusively owned and disposed by this component.
      /* eslint-disable react-hooks/immutability */
      for (const material of materialsRef.current) material.emissiveIntensity = pulse;
      /* eslint-enable react-hooks/immutability */
    }
  });

  const pivot =
    kind === "lung"
      ? ([0, 0.48, -0.08] as THREE.Vector3Tuple)
      : kind === "eye"
        ? ([-0.034, 0.81, 0.084] as THREE.Vector3Tuple)
        : ([0, 0, 0] as THREE.Vector3Tuple);

  return (
    <group
      ref={pivotRef}
      position={pivot}
      onClick={(event) => {
        event.stopPropagation();
        onSelect(MODEL_META[kind]);
      }}
      onPointerEnter={() => {
        document.body.style.cursor = "pointer";
      }}
      onPointerLeave={() => {
        document.body.style.cursor = "default";
      }}
    >
      <primitive
        object={model}
        position={[-pivot[0], -pivot[1], -pivot[2]]}
        dispose={null}
      />
    </group>
  );
}

function NerveOverlay({ stageId, stageProgress }: { stageId: StageId; stageProgress: number }) {
  const pulseRef = useRef<THREE.Mesh>(null);
  const curve = useMemo(
    () =>
      new THREE.CatmullRomCurve3([
        new THREE.Vector3(0.004, 0.77, 0.145),
        new THREE.Vector3(-0.028, 0.79, 0.112),
        new THREE.Vector3(-0.055, 0.77, 0.065),
        new THREE.Vector3(-0.045, 0.72, 0.022),
        new THREE.Vector3(-0.022, 0.68, -0.005),
      ]),
    [],
  );
  const geometry = useMemo(() => new THREE.TubeGeometry(curve, 80, 0.0022, 8, false), [curve]);
  useEffect(() => () => geometry.dispose(), [geometry]);
  const visible = stageId === "trigger" || stageId === "signal";

  useFrame(() => {
    if (!pulseRef.current || !visible) return;
    const t = stageId === "trigger" ? stageProgress * 0.55 : stageProgress;
    pulseRef.current.position.copy(curve.getPointAt(Math.min(0.995, t)));
  });

  if (!visible) return null;

  return (
    <group>
      <mesh geometry={geometry}>
        <meshBasicMaterial color="#8af0d4" transparent opacity={0.8} />
      </mesh>
      <mesh ref={pulseRef}>
        <icosahedronGeometry args={[0.007, 2]} />
        <meshBasicMaterial color="#dfff62" toneMapped={false} />
        <pointLight color="#dfff62" intensity={0.22} distance={0.12} />
      </mesh>
      <Html position={[-0.035, 0.69, 0.005]} center distanceFactor={2.1}>
        <div className="scene-tag scene-tag--schematic">Schematic neural pathway</div>
      </Html>
    </group>
  );
}

function TriggerOverlay({ stageProgress }: { stageProgress: number }) {
  const ref = useRef<THREE.Mesh>(null);
  useFrame(({ clock }) => {
    if (!ref.current) return;
    ref.current.rotation.x = clock.elapsedTime * 0.8;
    ref.current.rotation.y = clock.elapsedTime * 1.1;
    ref.current.position.z = 0.195 - Math.min(stageProgress * 0.05, 0.045);
  });
  return (
    <mesh ref={ref} position={[0.008, 0.765, 0.195]}>
      <dodecahedronGeometry args={[0.0085, 1]} />
      <meshStandardMaterial color="#e8ff83" emissive="#8aa71f" emissiveIntensity={2} roughness={0.8} />
    </mesh>
  );
}

function NasalMucosaOverlay({ progress }: { progress: number }) {
  const geometries = useMemo(() => {
    const curves = [
      [
        new THREE.Vector3(-0.006, 0.77, 0.158),
        new THREE.Vector3(-0.008, 0.778, 0.132),
        new THREE.Vector3(-0.01, 0.772, 0.102),
      ],
      [
        new THREE.Vector3(0.004, 0.782, 0.153),
        new THREE.Vector3(0.006, 0.791, 0.128),
        new THREE.Vector3(0.002, 0.786, 0.105),
      ],
      [
        new THREE.Vector3(0.013, 0.768, 0.151),
        new THREE.Vector3(0.015, 0.773, 0.126),
        new THREE.Vector3(0.011, 0.766, 0.105),
      ],
    ];
    return curves.map(
      (points) => new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), 28, 0.0023, 7, false),
    );
  }, []);
  useEffect(
    () => () => {
      for (const geometry of geometries) geometry.dispose();
    },
    [geometries],
  );

  return (
    <group>
      {geometries.map((geometry, index) => (
        <mesh geometry={geometry} key={index}>
          <meshBasicMaterial
            color={index === 1 ? "#ff9c8b" : "#d76267"}
            transparent
            opacity={0.5 + progress * 0.35}
          />
        </mesh>
      ))}
      <Html position={[0.075, 0.81, 0.115]} center distanceFactor={2.1}>
        <div className="scene-tag scene-tag--schematic">Nasal lining · schematic section</div>
      </Html>
    </group>
  );
}

function EyeDetailInset({
  stageId,
  stageProgress,
  onSelect,
}: {
  stageId: StageId;
  stageProgress: number;
  onSelect: (structure: SelectedStructure) => void;
}) {
  const { scene } = useGLTF(modelUrl("eye.glb"));
  const aspect = useThree((state) => state.size.width / state.size.height);
  const insetX = aspect < 1.05 ? 0.075 : 0.215;
  const insetScale = aspect < 1.05 ? 1.9 : 4.2;
  const globeRef = useRef<THREE.Group>(null);
  const upperLidRef = useRef<THREE.Mesh>(null);
  const lowerLidRef = useRef<THREE.Mesh>(null);
  const { model, modelMaterials } = useMemo(() => {
    const clone = scene.clone(true);
    const materials: THREE.MeshPhysicalMaterial[] = [];
    clone.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      if (object.name.toLowerCase().includes("palpebral_conjunctiva")) {
        object.visible = false;
        return;
      }
      const material = materialFor("eye", object.name || object.parent?.name || "eye");
      object.material = material;
      materials.push(material);
    });
    return { model: clone, modelMaterials: materials };
  }, [scene]);
  useEffect(
    () => () => {
      for (const material of modelMaterials) material.dispose();
    },
    [modelMaterials],
  );
  const [upperLidGeometry, lowerLidGeometry] = useMemo(() => {
    const upper = new THREE.Shape();
    upper.moveTo(-0.02, 0);
    upper.bezierCurveTo(-0.012, 0.012, 0.012, 0.012, 0.02, 0);
    upper.bezierCurveTo(0.01, -0.0015, -0.01, -0.0015, -0.02, 0);

    const lower = new THREE.Shape();
    lower.moveTo(-0.02, 0);
    lower.bezierCurveTo(-0.012, -0.01, 0.012, -0.01, 0.02, 0);
    lower.bezierCurveTo(0.01, 0.0015, -0.01, 0.0015, -0.02, 0);

    return [new THREE.ShapeGeometry(upper, 24), new THREE.ShapeGeometry(lower, 24)];
  }, []);
  useEffect(
    () => () => {
      upperLidGeometry.dispose();
      lowerLidGeometry.dispose();
    },
    [lowerLidGeometry, upperLidGeometry],
  );
  useFrame(() => {
    if (!globeRef.current || !upperLidRef.current || !lowerLidRef.current) return;
    let closure = 0;
    if (stageId === "compression") closure = THREE.MathUtils.smoothstep(stageProgress, 0.04, 0.28);
    if (stageId === "release") closure = 1 - THREE.MathUtils.smoothstep(stageProgress, 0.62, 0.92);
    globeRef.current.rotation.x = THREE.MathUtils.degToRad(1.2) * closure;
    globeRef.current.rotation.y = THREE.MathUtils.degToRad(1) * closure;
    globeRef.current.rotation.z = 0;
    upperLidRef.current.position.y = 0.005 * (1 - closure);
    lowerLidRef.current.position.y = -0.005 * (1 - closure);
  });

  return (
    <group
      position={[insetX, 0.79, 0.055]}
      onClick={(event) => {
        event.stopPropagation();
        onSelect(MODEL_META.eye);
      }}
      onPointerEnter={() => {
        document.body.style.cursor = "pointer";
      }}
      onPointerLeave={() => {
        document.body.style.cursor = "default";
      }}
    >
      {aspect >= 1.05 && (
        <Line
          points={[
            [-0.034, 0.81, 0.084],
            [0.09, 0.81, 0.08],
            [0.175, 0.79, 0.06],
          ]}
          position={[-insetX, -0.79, -0.055]}
          color="#73e7d1"
          transparent
          opacity={0.42}
          lineWidth={0.8}
        />
      )}
      <group ref={globeRef} scale={insetScale}>
        <primitive object={model} position={[0.034, -0.81, -0.084]} dispose={null} />
        <mesh ref={upperLidRef} geometry={upperLidGeometry} position={[0, 0.005, 0.0155]}>
          <meshPhysicalMaterial color="#9b7568" roughness={0.78} side={THREE.DoubleSide} transparent opacity={0.86} />
        </mesh>
        <mesh ref={lowerLidRef} geometry={lowerLidGeometry} position={[0, -0.005, 0.0157]}>
          <meshPhysicalMaterial color="#9b7568" roughness={0.78} side={THREE.DoubleSide} transparent opacity={0.86} />
        </mesh>
        <mesh position={[0, 0, 0.0145]}>
          <torusGeometry args={[0.0178, 0.00045, 8, 64]} />
          <meshBasicMaterial color="#ddff65" transparent opacity={0.65} />
        </mesh>
      </group>
      <Html position={[-0.025, 0.046, 0]} center distanceFactor={2}>
        <div className="scene-tag scene-tag--schematic">Right eye · NIH section + schematic lids · subtle downward + nasalward blink motion</div>
      </Html>
    </group>
  );
}

function PressureOverlay({ stageId, stageProgress }: { stageId: StageId; stageProgress: number }) {
  const fieldRef = useRef<THREE.Mesh>(null);
  const diaphragmRef = useRef<THREE.Mesh>(null);
  const active = stageId === "inspiration" || stageId === "compression" || stageId === "release";

  useFrame(() => {
    const field = fieldRef.current;
    const diaphragm = diaphragmRef.current;
    if (!field || !diaphragm) return;

    let intensity = 0;
    if (stageId === "inspiration") intensity = stageProgress * 0.22;
    if (stageId === "compression") intensity = 0.25 + stageProgress * 0.75;
    if (stageId === "release") intensity = Math.max(0, 1 - stageProgress * 1.45);
    (field.material as THREE.MeshBasicMaterial).opacity = intensity * 0.2;
    field.scale.setScalar(1 + intensity * 0.06);

    const diaphragmTravel =
      stageId === "inspiration"
        ? -stageProgress * 0.022
        : stageId === "release"
          ? -0.022 + stageProgress * 0.035
          : stageId === "compression"
            ? -0.022 + stageProgress * 0.01
            : 0;
    diaphragm.position.y = 0.355 + diaphragmTravel;
  });

  if (!active) return null;

  return (
    <group>
      <mesh ref={fieldRef} position={[0, 0.48, -0.07]} scale={[0.17, 0.22, 0.13]}>
        <sphereGeometry args={[1, 48, 32]} />
        <meshBasicMaterial color="#ff6f61" transparent opacity={0.08} depthWrite={false} side={THREE.BackSide} />
      </mesh>
      <mesh ref={diaphragmRef} position={[0, 0.355, -0.075]} scale={[0.14, 0.036, 0.105]}>
        <sphereGeometry args={[1, 64, 24, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshPhysicalMaterial color="#6ee7d0" transparent opacity={0.34} roughness={0.62} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
      <Html position={[0.13, 0.34, -0.02]} center distanceFactor={2.3}>
        <div className="scene-tag scene-tag--schematic">Diaphragm motion overlay</div>
      </Html>
    </group>
  );
}

function GlottisOverlay({ stageId, stageProgress }: { stageId: StageId; stageProgress: number }) {
  const leftFoldRef = useRef<THREE.Mesh>(null);
  const rightFoldRef = useRef<THREE.Mesh>(null);
  const visible =
    stageId === "inspiration" || stageId === "compression" || stageId === "release";

  useFrame(() => {
    if (!leftFoldRef.current || !rightFoldRef.current || !visible) return;
    let opening = 1;
    if (stageId === "compression") {
      opening = 1 - THREE.MathUtils.smoothstep(stageProgress, 0.02, 0.24);
    }
    if (stageId === "release") {
      opening = THREE.MathUtils.smoothstep(stageProgress, 0, 0.08);
    }
    const halfGap = 0.001 + opening * 0.006;
    leftFoldRef.current.position.x = -halfGap - 0.0035;
    rightFoldRef.current.position.x = halfGap + 0.0035;
  });

  if (!visible) return null;

  const state =
    stageId === "compression" ? "closing → closed" : stageId === "release" ? "opening" : "open";

  return (
    <group position={[0, 0.678, 0.128]} scale={1.35}>
      <mesh position={[0, 0, -0.003]}>
        <circleGeometry args={[0.023, 40]} />
        <meshBasicMaterial color="#120708" transparent opacity={0.72} depthWrite={false} />
      </mesh>
      <mesh ref={leftFoldRef} position={[-0.0105, 0, 0]}>
        <capsuleGeometry args={[0.0037, 0.025, 6, 16]} />
        <meshPhysicalMaterial color="#f2a0a0" roughness={0.58} emissive="#5b1119" emissiveIntensity={0.55} />
      </mesh>
      <mesh ref={rightFoldRef} position={[0.0105, 0, 0]}>
        <capsuleGeometry args={[0.0037, 0.025, 6, 16]} />
        <meshPhysicalMaterial color="#f2a0a0" roughness={0.58} emissive="#5b1119" emissiveIntensity={0.55} />
      </mesh>
      <mesh position={[0, 0, 0.001]}>
        <torusGeometry args={[0.023, 0.0007, 8, 48]} />
        <meshBasicMaterial color="#ddff65" transparent opacity={0.58} />
      </mesh>
      <Html position={[0.076, 0.018, 0]} center distanceFactor={2.1}>
        <div className="scene-tag scene-tag--schematic">Glottal aperture · schematic top view · {state}</div>
      </Html>
    </group>
  );
}

function AirflowOverlay({ progress }: { progress: number }) {
  const pointsRef = useRef<THREE.Points>(null);
  const materialRef = useRef<THREE.PointsMaterial>(null);
  const positions = useMemo(() => {
    const count = 520;
    const values = new Float32Array(count * 3);
    let seed = 918273;
    const random = () => {
      seed = (seed * 16807) % 2147483647;
      return (seed - 1) / 2147483646;
    };
    for (let index = 0; index < count; index += 1) {
      const depth = Math.pow(random(), 0.75) * 0.78;
      const spread = 0.012 + depth * 0.13;
      values[index * 3] = (random() - 0.5) * spread;
      values[index * 3 + 1] = 0.72 + (random() - 0.48) * spread * 0.72;
      values[index * 3 + 2] = 0.155 + depth;
    }
    return values;
  }, []);
  const ribbonGeometries = useMemo(() => {
    const offsets = [
      [-0.012, 0.008],
      [0.006, -0.004],
      [0.018, 0.012],
      [-0.02, -0.01],
    ];
    return offsets.map(([x, y], index) => {
      const curve = new THREE.CatmullRomCurve3([
        new THREE.Vector3(x * 0.2, 0.72 + y * 0.2, 0.158),
        new THREE.Vector3(x, 0.72 + y, 0.22 + index * 0.005),
        new THREE.Vector3(x * 1.8, 0.72 + y * 1.4, 0.34 + index * 0.014),
        new THREE.Vector3(x * 2.7, 0.72 + y * 2.2, 0.47 + index * 0.025),
      ]);
      return new THREE.TubeGeometry(curve, 44, 0.0018 - index * 0.00018, 6, false);
    });
  }, []);
  const sheetGeometry = useMemo(() => {
    const shape = new THREE.Shape();
    shape.moveTo(-0.018, -0.012);
    shape.bezierCurveTo(-0.03, 0.004, -0.008, 0.025, 0.016, 0.017);
    shape.bezierCurveTo(0.034, 0.01, 0.026, -0.018, -0.018, -0.012);
    return new THREE.ShapeGeometry(shape, 20);
  }, []);
  useEffect(
    () => () => {
      sheetGeometry.dispose();
      for (const geometry of ribbonGeometries) geometry.dispose();
    },
    [ribbonGeometries, sheetGeometry],
  );

  useFrame(({ clock }) => {
    if (!pointsRef.current || !materialRef.current) return;
    pointsRef.current.position.z = (clock.elapsedTime * 0.04) % 0.08;
    materialRef.current.opacity = Math.sin(Math.min(progress, 1) * Math.PI) * 0.78;
  });

  return (
    <group>
      <mesh geometry={sheetGeometry} position={[0, 0.72, 0.172]} scale={0.55 + progress * 0.9}>
        <meshBasicMaterial
          color="#ffc0b2"
          transparent
          opacity={Math.sin(Math.min(progress * 1.8, 1) * Math.PI) * 0.32}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
      {ribbonGeometries.map((geometry, index) => (
        <mesh geometry={geometry} key={index}>
          <meshBasicMaterial
            color={index % 2 === 0 ? "#ffc8ba" : "#dfffe3"}
            transparent
            opacity={Math.sin(Math.min(progress * 1.45, 1) * Math.PI) * 0.48}
            depthWrite={false}
          />
        </mesh>
      ))}
      <points ref={pointsRef}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        </bufferGeometry>
        <pointsMaterial ref={materialRef} color="#ffd1c7" size={0.006} sizeAttenuation transparent opacity={0.7} depthWrite={false} blending={THREE.AdditiveBlending} />
      </points>
      <Html position={[-0.34, 0.70, 0.56]} center distanceFactor={2.5}>
        <div className="scene-tag scene-tag--schematic">Representative airflow + fluid fragmentation</div>
      </Html>
    </group>
  );
}

function GuidedCamera({
  focus,
  exploring,
  reducedMotion,
  motionActive,
  onExplore,
}: {
  focus: SneezeStage["focus"];
  exploring: boolean;
  reducedMotion: boolean;
  motionActive: boolean;
  onExplore: () => void;
}) {
  const controlsRef = useRef<OrbitControlsImpl>(null);
  const { camera } = useThree();
  const preset = CAMERA_PRESETS[focus];
  const position = useMemo(() => new THREE.Vector3(...preset.position), [preset]);
  const target = useMemo(() => new THREE.Vector3(...preset.target), [preset]);

  useFrame((_, delta) => {
    const controls = controlsRef.current;
    if (!controls || exploring) return;
    const damping = reducedMotion || !motionActive ? 1 : 1 - Math.pow(0.003, delta);
    camera.position.lerp(position, damping);
    controls.target.lerp(target, damping);
    controls.update();
  });

  return (
    <OrbitControls
      ref={controlsRef}
      makeDefault
      enableDamping
      dampingFactor={0.06}
      enablePan={false}
      minDistance={1.25}
      maxDistance={8.5}
      minPolarAngle={0.38}
      maxPolarAngle={Math.PI - 0.38}
      onStart={onExplore}
    />
  );
}

function AnatomyWorld(props: AnatomySceneProps) {
  return (
    <>
      <color attach="background" args={["#050708"]} />
      <fog attach="fog" args={["#050708", 4.7, 10]} />
      <ambientLight intensity={0.6} color="#a8c4d1" />
      <hemisphereLight args={["#d8ebf1", "#1b0908", 1.7]} />
      <spotLight position={[-4, 6, 6]} angle={0.34} penumbra={0.8} intensity={90} color="#ffd0bd" />
      <spotLight position={[4, 3, -4]} angle={0.45} penumbra={0.9} intensity={75} color="#79dce8" />
      <pointLight position={[0, -1, 3]} intensity={18} color="#ff5d56" distance={7} />

      <GuidedCamera
        focus={props.focus}
        exploring={props.exploring}
        reducedMotion={props.reducedMotion}
        motionActive={props.motionActive}
        onExplore={props.onExplore}
      />

      <group scale={2.65} position={[0, -0.04, 0]}>
        {props.layers.surface && (
          <ModelLayer
            url={modelUrl("skin.glb")}
            kind="skin"
            opacity={props.surfaceOpacity}
            stageId={props.stageId}
            stageProgress={props.stageProgress}
            onSelect={props.onSelect}
          />
        )}
        {props.layers.respiratory && (
          <ModelLayer
            url={modelUrl("lung.glb")}
            kind="lung"
            stageId={props.stageId}
            stageProgress={props.stageProgress}
            onSelect={props.onSelect}
          />
        )}
        {props.layers.respiratory && (
          <ModelLayer
            url={modelUrl("trachea.glb")}
            kind="trachea"
            stageId={props.stageId}
            stageProgress={props.stageProgress}
            onSelect={props.onSelect}
          />
        )}
        {props.layers.larynx && (
          <ModelLayer
            url={modelUrl("larynx.glb")}
            kind="larynx"
            stageId={props.stageId}
            stageProgress={props.stageProgress}
            onSelect={props.onSelect}
          />
        )}
        {props.layers.larynx && (
          <GlottisOverlay stageId={props.stageId} stageProgress={props.stageProgress} />
        )}
        {props.layers.mouth && (
          <Suspense fallback={null}>
            <ModelLayer
              url={modelUrl("mouth.glb")}
              kind="mouth"
              stageId={props.stageId}
              stageProgress={props.stageProgress}
              onSelect={props.onSelect}
            />
          </Suspense>
        )}
        {props.layers.ocular && (
          <Suspense fallback={null}>
            <EyeDetailInset
              stageId={props.stageId}
              stageProgress={props.stageProgress}
              onSelect={props.onSelect}
            />
          </Suspense>
        )}
        {props.layers.nervous && (
          <NerveOverlay stageId={props.stageId} stageProgress={props.stageProgress} />
        )}
        {props.stageId === "trigger" && (
          <>
            <TriggerOverlay stageProgress={props.stageProgress} />
            <NasalMucosaOverlay progress={props.stageProgress} />
          </>
        )}
        {props.layers.pressure && (
          <PressureOverlay stageId={props.stageId} stageProgress={props.stageProgress} />
        )}
        {props.layers.pressure && props.stageId === "release" && (
          <AirflowOverlay progress={props.stageProgress} />
        )}
      </group>
    </>
  );
}

function SceneFallback() {
  return (
    <Html center>
      <div className="scene-loader" role="status">
        <span className="scene-loader__ring" />
        <strong>Assembling NIH anatomy</strong>
        <small>Loading aligned reference objects</small>
      </div>
    </Html>
  );
}

export function AnatomyScene(props: AnatomySceneProps) {
  return (
    <SceneErrorBoundary>
      {(retryKey) => (
        <Canvas
          key={retryKey}
          frameloop={props.motionActive ? "always" : "demand"}
          camera={{ position: CAMERA_PRESETS[props.focus].position, fov: 30, near: 0.01, far: 50 }}
          dpr={[1, 1.75]}
          gl={{ antialias: true, alpha: false, powerPreference: "high-performance" }}
          onPointerMissed={() => {
            document.body.style.cursor = "default";
          }}
        >
          <Suspense fallback={<SceneFallback />}>
            <AnatomyWorld {...props} />
          </Suspense>
        </Canvas>
      )}
    </SceneErrorBoundary>
  );
}

useGLTF.preload(modelUrl("skin.glb"));

const journeyPreloadGroups = [
  { delayMs: 4_000, files: ["lung.glb", "trachea.glb", "larynx.glb"] },
  { delayMs: 16_000, files: ["eye.glb"] },
  { delayMs: 25_000, files: ["mouth.glb"] },
] as const;
const journeyModelFiles = journeyPreloadGroups.flatMap((group) => group.files);
let journeyPreloadScheduled = false;

export function preloadJourneyModels() {
  if (journeyPreloadScheduled) return;
  journeyPreloadScheduled = true;
  for (const group of journeyPreloadGroups) {
    window.setTimeout(() => {
      for (const file of group.files) useGLTF.preload(modelUrl(file));
    }, group.delayMs);
  }
}

function clearModelCache() {
  for (const file of ["skin.glb", ...journeyModelFiles]) {
    useGLTF.clear(modelUrl(file));
  }
}

class SceneErrorBoundary extends Component<
  { children: (retryKey: number) => ReactNode },
  { error: Error | null; retryKey: number }
> {
  state = { error: null as Error | null, retryKey: 0 };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  retry = () => {
    clearModelCache();
    this.setState((current) => ({ error: null, retryKey: current.retryKey + 1 }));
  };

  render() {
    if (this.state.error) {
      return (
        <div className="scene-error" role="alert">
          <strong>Anatomy model could not be loaded.</strong>
          <span>The timeline and evidence remain available.</span>
          <button type="button" onClick={this.retry}>Retry 3D anatomy</button>
        </div>
      );
    }
    return this.props.children(this.state.retryKey);
  }
}
